import jwtDecode from 'jwt-decode';
import User from './user';
import Cookie from './cookie';
import Cognito from './cognito';
import fetch from '../core/fetch';
import ErrorMessage from './error-message';

class UserFactory {
  static constructCreateUserURL() {
    return process.env.REACT_APP_CLIENT_FC_USER_API;
  }

  static constructGetUserURL(id) {
    return `${process.env.REACT_APP_CLIENT_FC_USER_API}/${id}`;
  }

  static constructCreateTokenURL() {
    return `${process.env.REACT_APP_CLIENT_FC_TOKEN_API}`;
  }

  static constructCreateProfileURL() {
    return `${process.env.REACT_APP_CLIENT_FC_PROFILE_API}`;
  }

  static constructProfileUpdateURL(id) {
    return `${process.env.REACT_APP_CLIENT_FC_PROFILE_API}/${id}`;
  }

  static constructGetProfileURL() {
    return `${process.env.REACT_APP_CLIENT_FC_PROFILE_API}/me`;
  }

  static constructForgotPasswordEmailURL() {
    return `${process.env.REACT_APP_CLIENT_FC_EMAIL_API}/forgot-password`;
  }

  static constructSubscriptionStatusURL(userID) {
    return `${process.env.REACT_APP_CLIENT_FC_SUBSCRIPTIONS_API}/subscriptions/status?user-id=${userID}`;
  }

  static constructValidateTokenURL() {
    return `${process.env.REACT_APP_CLIENT_FC_TOKEN_API}/validate`;
  }

  static constructResetPasswordURL(id) {
    return `${process.env.REACT_APP_CLIENT_FC_USER_API}/${id}/password`;
  }

  static async parseResponse(response) {
    const payload = await response.json();
    if (
      response.status >= 200 &&
      response.status < 300 &&
      payload &&
      payload.data &&
      payload.data.attributes
    ) {
      return { id: payload.data.id, ...payload.data.attributes };
    }
    if (Array.isArray(payload.errors) && payload.errors[0].code && payload.errors[0].detail) {
      const error = new Error(payload.errors[0].detail);
      error.code = payload.errors[0].code;
      error.message = ErrorMessage.map(error.code, error.message);
      throw error;
    } else if (response.status >= 200 && response.status < 300 && payload) {
      return { id: 'status', ...payload };
    }
    throw new Error(`Invalid payload: ${JSON.stringify(payload)}`);
  }

  static async createUser(email, password, firstName, lastName, source, touAccepted) {
    const countryCode = Cookie.getCountry();
    const response = await fetch(this.constructCreateUserURL(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'X-IPCountry': countryCode,
      },
      body: JSON.stringify({
        data: {
          attributes: {
            'first-name': firstName,
            'last-name': lastName,
            email,
            password,
            'tou-accepted': touAccepted,
            source,
          },
        },
      }),
    });
    return this.parseResponse(response);
  }

  static async createToken(email, password) {
    const countryCode = Cookie.getCountry();
    const response = await fetch(this.constructCreateTokenURL(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'X-IPCountry': countryCode,
      },
      body: JSON.stringify({
        data: {
          attributes: {
            email,
            password,
          },
        },
      }),
    });
    return this.parseResponse(response);
  }

  static async createProfile(profileAttributes, token) {
    const response = await fetch(this.constructCreateProfileURL(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        data: {
          attributes: {
            ...profileAttributes,
          },
        },
      }),
    });
    return this.parseResponse(response);
  }

  static async checkSubscriptionStatus(token, userID) {
    try {
      const response = await fetch(this.constructSubscriptionStatusURL(userID), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 404) {
        return { play: { status: 'unsubscribed' } };
      }
      return this.parseResponse(response);
    } catch (err) {
      return err;
    }
  }

  static async getUser(token) {
    const validatedToken = await this.validateToken(token);
    const response = await fetch(this.constructGetUserURL(validatedToken.id), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return this.parseResponse(response);
  }

  static async validateToken(token) {
    try {
      const response = await fetch(this.constructValidateTokenURL(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return await this.parseResponse(response);
    } catch (err) {
      return err;
    }
  }

  static async getProfile(token) {
    try {
      const response = await fetch(this.constructGetProfileURL(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return await this.parseResponse(response);
    } catch (err) {
      if (err.code === '404') {
        const data = await this.createProfile([], token);
        return this.parseResponse(data);
      }
      return err;
    }
  }

  static async updateProfile(profileID, attributes) {
    const session = await Cognito.fetchCognitoCredentials();
    if (!session) {
      return null;
    }
    const { token } = session;
    const response = await fetch(this.constructProfileUpdateURL(profileID), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        data: {
          attributes,
        },
      }),
    });
    return this.parseResponse(response);
  }

  static async sendForgotPasswordEmail(email, source = null) {
    const response = await fetch(this.constructForgotPasswordEmailURL(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
      },
      body: JSON.stringify({
        data: {
          attributes: {
            email,
            source,
          },
        },
      }),
    });
    return this.parseResponse(response);
  }

  static async checkForExistingSession() {
    const session = await Cognito.fetchCognitoCredentials();
    if (!session) {
      return null;
    }
    const [authInfo, profile] = await Promise.all([
      this.checkSubscriptionStatus(session.token, session.userID),
      this.getProfile(session.token),
    ]);
    const user = new User(
      session.token,
      session.userID,
      session.email,
      session['first-name'],
      session['last-name'],
      { play: authInfo.play.status, createdAt: authInfo.play['created-at'] },
    );
    if (profile) {
      user.setProfile(profile);
    }
    return user;
  }

  static async create(email, password, firstName, lastName, source, touAccepted = true) {
    if (!email || !password || !firstName || !lastName) {
      throw new Error('Email, Password, First and Last Name required for creating an account');
    }
    const newUser = await this.createUser(
      email,
      password,
      firstName,
      lastName,
      source,
      touAccepted,
    );
    const token = await this.createToken(email, password);
    const profile = await this.createProfile({}, token.token);

    const user = new User(
      token.token,
      token['user-id'],
      newUser.email,
      newUser['first-name'],
      newUser['last-name'],
      token.subscriptions,
    );
    if (profile) {
      user.setProfile(profile);
    }
    Cookie.setSession(token['user-id'], user.token);
    return user;
  }

  static async authenticate(email, password) {
    if (!email || !password) {
      throw new Error('Email and Password required for authentication');
    }
    const token = await this.createToken(email, password);
    const newUser = jwtDecode(token.token);
    const profile = await this.getProfile(token.token);
    const user = new User(
      token.token,
      token['user-id'],
      newUser.email,
      newUser['first-name'],
      newUser['last-name'],
      token.subscriptions,
    );
    if (profile) {
      user.setProfile(profile);
    }
    Cookie.setSession(token['user-id'], user.token);
    return user;
  }

  static async forgotPassword(email, source = null) {
    const forgotPassword = await this.sendForgotPasswordEmail(email, source);
    return forgotPassword.message;
  }

  static async resetPassword(userID, password, token) {
    const response = await fetch(this.constructResetPasswordURL(userID), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/vnd.api+json',
      },
      body: JSON.stringify({
        data: {
          attributes: {
            new: password,
          },
        },
      }),
    });
    return this.parseResponse(response);
  }
}

export default UserFactory;
